import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import AuthProvider, { useAuth } from './contexts/AuthContext';

// Import existing pages
import Dashboard from './pages/dashboard';
import InvoiceEditor from './pages/invoice-editor';
import Uploads from './pages/uploads';
import Invoices from './pages/invoices';
import Vendors from './pages/vendors';
import AuditTrail from './pages/audit-trail';
import MatchingRules from './pages/matching-rules';
import Settings from './pages/settings';
import ExportHistory from './pages/export-history';
import PriceReview from './pages/price-review';
import PriceReviewRules from './pages/price-review-rules';
import CalendarView from './pages/calendar-view';
import ProcessedInvoices from './pages/processed-invoices';
import NotFound from './pages/NotFound';
import Superadmin from './pages/superadmin';

// Import auth pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, currentOrgId, isSuperadmin } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  // If user has no org and is not superadmin, block access until assigned
  if (!isSuperadmin && !currentOrgId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md text-center p-6 border border-border rounded-lg bg-card">
          <h2 className="text-xl font-semibold text-foreground mb-2">Awaiting Organization Assignment</h2>
          <p className="text-sm text-muted-foreground">
            Your account is active but not assigned to an organization yet. A superadmin must assign you to an org before you can use the app.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

const SuperadminRoute = ({ children }) => {
  const { user, loading, isSuperadmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  if (!isSuperadmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to dashboard if already authenticated
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ScrollToTop />
          <RouterRoutes>
            {/* Root: send authenticated users to dashboard, others to login */}
            <Route path="/" element={<RootRedirect />} />

            {/* Public auth routes */}
            <Route
              path="/auth/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/signup"
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              }
            />

            {/* Protected app routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice-editor"
              element={
                <ProtectedRoute>
                  <InvoiceEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/uploads"
              element={
                <ProtectedRoute>
                  <Uploads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <Invoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/processed-invoices"
              element={
                <ProtectedRoute>
                  <ProcessedInvoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <Vendors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-trail"
              element={
                <ProtectedRoute>
                  <AuditTrail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matching-rules"
              element={
                <ProtectedRoute>
                  <MatchingRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/price-review"
              element={
                <ProtectedRoute>
                  <PriceReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/price-review/rules"
              element={
                <ProtectedRoute>
                  <PriceReviewRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/export-history"
              element={
                <ProtectedRoute>
                  <ExportHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar-view"
              element={
                <ProtectedRoute>
                  <CalendarView />
                </ProtectedRoute>
              }
            />

            {/* Superadmin Only */}
            <Route
              path="/superadmin"
              element={
                <SuperadminRoute>
                  <Superadmin />
                </SuperadminRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default Routes;

// Decides landing route based on auth state without hanging
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth/login" replace />;
};
